import { createContext, useContext, useReducer, useEffect, useCallback, useState } from 'react';
import * as SocialAPI from '../services/SocialAPI';
import * as CollectionAPI from '../services/CollectionAPI';
import { useCollectionContext } from './CollectionContext';
import { overrideKey } from '../utils/tradeables';

// Social state (wishlist now; friends/trades in later phases) lives in its
// own provider rather than CollectionContext — different lifecycle: lazy
// loads, optimistic mutations, no auto-save effect loops.

const ActionTypes = {
  SET_WISHLIST: 'SET_WISHLIST',
  UPSERT_WISHLIST_ITEM: 'UPSERT_WISHLIST_ITEM',
  REMOVE_WISHLIST_ITEM: 'REMOVE_WISHLIST_ITEM',
  SET_FRIENDS: 'SET_FRIENDS',
  SET_TRADE_OVERRIDES: 'SET_TRADE_OVERRIDES',
  UPSERT_TRADE_OVERRIDE: 'UPSERT_TRADE_OVERRIDE',
  REMOVE_TRADE_OVERRIDE: 'REMOVE_TRADE_OVERRIDE',
  SET_MATCHES: 'SET_MATCHES',
  SET_TRADES: 'SET_TRADES'
};

const initialState = {
  // Keyed `${cardId}|${variant}` (see overrideKey) — wishlist is per
  // variant, not per card
  wishlist: {},
  wishlistLoaded: false,
  // Friends: accepted + pending requests in both directions
  friends: [],
  incoming: [],
  outgoing: [],
  friendsLoaded: false,
  // Manual trade overrides keyed `${cardId}|${variant}` — tradeable
  // quantities themselves are derived (see utils/tradeables.js)
  tradeOverrides: {},
  tradeOverridesLoaded: false,
  // Per-friend trade matches, ranked by score (server-computed)
  matches: [],
  matchesLoaded: false,
  // Trade proposals I'm part of
  trades: [],
  tradesHistory: [],
  tradesLoaded: false
};

const socialReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_WISHLIST: {
      const wishlist = {};
      for (const item of action.payload) wishlist[overrideKey(item.cardId, item.variant)] = item;
      return { ...state, wishlist, wishlistLoaded: true };
    }

    case ActionTypes.UPSERT_WISHLIST_ITEM:
      return {
        ...state,
        wishlist: {
          ...state.wishlist,
          [overrideKey(action.payload.cardId, action.payload.variant)]: action.payload
        }
      };

    case ActionTypes.REMOVE_WISHLIST_ITEM: {
      const next = { ...state.wishlist };
      delete next[overrideKey(action.payload.cardId, action.payload.variant)];
      return { ...state, wishlist: next };
    }

    case ActionTypes.SET_FRIENDS:
      return {
        ...state,
        friends: action.payload.friends || [],
        incoming: action.payload.incoming || [],
        outgoing: action.payload.outgoing || [],
        friendsLoaded: true
      };

    case ActionTypes.SET_TRADE_OVERRIDES: {
      const tradeOverrides = {};
      for (const o of action.payload) {
        tradeOverrides[overrideKey(o.cardId, o.variant)] = {
          mode: o.mode,
          offerQty: o.offerQty ?? null
        };
      }
      return { ...state, tradeOverrides, tradeOverridesLoaded: true };
    }

    case ActionTypes.UPSERT_TRADE_OVERRIDE: {
      const { cardId, variant, mode, offerQty } = action.payload;
      return {
        ...state,
        tradeOverrides: {
          ...state.tradeOverrides,
          [overrideKey(cardId, variant)]: { mode, offerQty: offerQty ?? null }
        }
      };
    }

    case ActionTypes.REMOVE_TRADE_OVERRIDE: {
      const next = { ...state.tradeOverrides };
      delete next[overrideKey(action.payload.cardId, action.payload.variant)];
      return { ...state, tradeOverrides: next };
    }

    case ActionTypes.SET_MATCHES:
      return { ...state, matches: action.payload, matchesLoaded: true };

    case ActionTypes.SET_TRADES:
      return {
        ...state,
        trades: action.payload.active,
        tradesHistory: action.payload.history,
        tradesLoaded: true
      };

    default:
      return state;
  }
};

const SocialContext = createContext();

export const SocialProvider = ({ children }) => {
  const [state, dispatch] = useReducer(socialReducer, initialState);
  const { setCollection } = useCollectionContext();

  // Proposal builder launcher — seed: { friend: {userId, username}, item? }
  // or null when closed. Rendered at app level (see App.jsx).
  const [proposalSeed, setProposalSeed] = useState(null);
  const openProposalBuilder = useCallback((seed = {}) => setProposalSeed(seed), []);
  const closeProposalBuilder = useCallback(() => setProposalSeed(null), []);

  const refreshFriends = useCallback(async () => {
    try {
      const data = await SocialAPI.getFriends();
      dispatch({ type: ActionTypes.SET_FRIENDS, payload: data });
      return data;
    } catch (err) {
      console.error('Failed to load friends:', err);
      return null;
    }
  }, []);

  const refreshTrades = useCallback(async () => {
    try {
      const [active, history] = await Promise.all([
        SocialAPI.getTrades('active'),
        SocialAPI.getTrades('history')
      ]);
      dispatch({ type: ActionTypes.SET_TRADES, payload: { active, history } });
    } catch (err) {
      console.error('Failed to load trades:', err);
    }
  }, []);

  const refreshMatches = useCallback(async () => {
    try {
      const matches = await SocialAPI.getMatches();
      dispatch({ type: ActionTypes.SET_MATCHES, payload: matches });
      return matches;
    } catch (err) {
      console.error('Failed to load trade matches:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    SocialAPI.getWishlist()
      .then((items) => dispatch({ type: ActionTypes.SET_WISHLIST, payload: items }))
      .catch((err) => console.error('Failed to load wishlist:', err));
    SocialAPI.getTradeables()
      .then((data) => dispatch({ type: ActionTypes.SET_TRADE_OVERRIDES, payload: data.overrides }))
      .catch((err) => console.error('Failed to load trade overrides:', err));
    refreshFriends();
    refreshMatches();
    refreshTrades();
  }, [refreshFriends, refreshMatches, refreshTrades]);

  const isWishlisted = useCallback(
    (cardId, variant) => Boolean(state.wishlist[overrideKey(cardId, variant)]),
    [state.wishlist]
  );

  // True if ANY variant of this card is wishlisted — drives the card-grid
  // star badge, which has no room to show per-variant state.
  const isCardWishlisted = useCallback(
    (cardId) => Object.keys(state.wishlist).some((key) => key.startsWith(`${cardId}|`)),
    [state.wishlist]
  );

  const getWishlistItem = useCallback(
    (cardId, variant) => state.wishlist[overrideKey(cardId, variant)] || null,
    [state.wishlist]
  );

  // Optimistic add/update — state first, fire-and-forget save (same posture
  // as useCollection.updateCard).
  const addToWishlist = useCallback((card, variant, priority = 'medium') => {
    const item = {
      cardId: card.id,
      variant,
      priority,
      ...SocialAPI.cardSnapshot(card),
      createdAt: new Date().toISOString()
    };
    dispatch({ type: ActionTypes.UPSERT_WISHLIST_ITEM, payload: item });
    SocialAPI.addToWishlist(card, variant, priority).catch((err) =>
      console.error('Failed to save wishlist item:', err)
    );
  }, []);

  const setWishlistPriority = useCallback((item, priority) => {
    dispatch({ type: ActionTypes.UPSERT_WISHLIST_ITEM, payload: { ...item, priority } });
    SocialAPI.setWishlistPriority(item, priority).catch((err) =>
      console.error('Failed to update wishlist priority:', err)
    );
  }, []);

  const removeFromWishlist = useCallback((cardId, variant) => {
    dispatch({ type: ActionTypes.REMOVE_WISHLIST_ITEM, payload: { cardId, variant } });
    SocialAPI.removeFromWishlist(cardId, variant).catch((err) =>
      console.error('Failed to remove wishlist item:', err)
    );
  }, []);

  const toggleWishlist = useCallback(
    (card, variant, priority = 'medium') => {
      if (state.wishlist[overrideKey(card.id, variant)]) {
        removeFromWishlist(card.id, variant);
      } else {
        addToWishlist(card, variant, priority);
      }
    },
    [state.wishlist, addToWishlist, removeFromWishlist]
  );

  // Friend mutations await the server (the server decides outcomes like
  // auto-accepting a reverse request), then refresh the whole friends slice.
  const sendFriendRequest = useCallback(
    async (username) => {
      const result = await SocialAPI.sendFriendRequest(username);
      await refreshFriends();
      return result;
    },
    [refreshFriends]
  );

  const acceptFriendRequest = useCallback(
    async (friendshipId) => {
      await SocialAPI.acceptFriendRequest(friendshipId);
      await refreshFriends();
    },
    [refreshFriends]
  );

  const removeFriendship = useCallback(
    async (friendshipId) => {
      await SocialAPI.removeFriendship(friendshipId);
      await refreshFriends();
    },
    [refreshFriends]
  );

  // Trade overrides — optimistic + fire-and-forget, like wishlist
  const setTradeOverride = useCallback((cardId, variant, mode, offerQty = null) => {
    dispatch({ type: ActionTypes.UPSERT_TRADE_OVERRIDE, payload: { cardId, variant, mode, offerQty } });
    SocialAPI.setTradeOverride(cardId, variant, mode, offerQty).catch((err) =>
      console.error('Failed to save trade override:', err)
    );
  }, []);

  const clearTradeOverride = useCallback((cardId, variant) => {
    dispatch({ type: ActionTypes.REMOVE_TRADE_OVERRIDE, payload: { cardId, variant } });
    SocialAPI.removeTradeOverride(cardId, variant).catch((err) =>
      console.error('Failed to remove trade override:', err)
    );
  }, []);

  // Trade proposal actions await the server (the state machine decides),
  // then refresh. Completing a trade also refetches the collection — the
  // server adjusted quantities on both sides; never mirror that client-side.
  const createTrade = useCallback(
    async (recipientId, items) => {
      const trade = await SocialAPI.createTrade(recipientId, items);
      await refreshTrades();
      return trade;
    },
    [refreshTrades]
  );

  const actOnTrade = useCallback(
    async (tradeId, action) => {
      await SocialAPI.actOnTrade(tradeId, action);
      const refreshes = [refreshTrades()];
      if (action === 'complete') {
        refreshes.push(
          CollectionAPI.getCollection().then(setCollection),
          SocialAPI.getWishlist().then((items) =>
            dispatch({ type: ActionTypes.SET_WISHLIST, payload: items })
          ),
          refreshMatches()
        );
      }
      await Promise.all(refreshes);
    },
    [refreshTrades, refreshMatches, setCollection]
  );

  const value = {
    wishlist: state.wishlist,
    wishlistItems: Object.values(state.wishlist),
    wishlistLoaded: state.wishlistLoaded,
    isWishlisted,
    isCardWishlisted,
    getWishlistItem,
    addToWishlist,
    setWishlistPriority,
    removeFromWishlist,
    toggleWishlist,

    friends: state.friends,
    incoming: state.incoming,
    outgoing: state.outgoing,
    friendsLoaded: state.friendsLoaded,
    refreshFriends,
    sendFriendRequest,
    acceptFriendRequest,
    removeFriendship,

    tradeOverrides: state.tradeOverrides,
    tradeOverridesLoaded: state.tradeOverridesLoaded,
    setTradeOverride,
    clearTradeOverride,

    matches: state.matches,
    matchesLoaded: state.matchesLoaded,
    refreshMatches,

    trades: state.trades,
    tradesHistory: state.tradesHistory,
    tradesLoaded: state.tradesLoaded,
    refreshTrades,
    createTrade,
    actOnTrade,

    proposalSeed,
    openProposalBuilder,
    closeProposalBuilder
  };

  return <SocialContext.Provider value={value}>{children}</SocialContext.Provider>;
};

export const useSocialContext = () => {
  const context = useContext(SocialContext);
  if (!context) {
    throw new Error('useSocialContext must be used within a SocialProvider');
  }
  return context;
};

export default SocialContext;
